/**
 * G06-WIRE. Pure helpers for wiring Pathfinder into the horizontal battle scene.
 *
 * Why a separate module: HorizontalBattleScene is a Phaser scene full of
 * `this.add.graphics()` calls that can't run under vitest without a canvas.
 * The repath logic — which boss bbox to use, which enemies to repath this
 * tick, what to do when boss is dead — is pure data manipulation and can be
 * unit-tested directly. The scene just calls these helpers.
 *
 * Spec: docs/GAME_TECHNIQUES_WIKI.md G06 — A* drop-in. Boss bbox is the
 * obstacle list (TileMap-less fallback path called out in pathfinder.ts).
 * Repath cadence is 500ms per spec; we round-robin within that window so a
 * single tick never fires 20 A* searches (60FPS budget = 16ms).
 *
 * Phaser-free. All inputs are plain numbers / interfaces — pass scene state
 * in, get plans out. The scene drives the side effects.
 */
import type { Pathfinder, ObstacleRect, PathResult } from '../ai/pathfinder';

/**
 * Compute the boss obstacle list. When the boss is null, dead, or hasn't yet
 * arrived at its resting x, returns []. Otherwise returns a single AABB rect
 * sized to the boss sprite (with a small padding so enemies don't graze the
 * edge and clip through corners).
 *
 * Padding is in world px. 4px = half an enemy width — keeps the A* grid path
 * one cell outside the boss visual footprint.
 */
export function computeBossObstacles(
  boss: { x: number; y: number; alive: boolean; def: { size?: number } } | null,
  options: { arrived?: boolean; padding?: number } = {},
): ObstacleRect[] {
  if (!boss) return [];
  if (!boss.alive) return [];
  if (options.arrived === false) return [];
  const size = boss.def.size ?? 96;
  const pad = options.padding ?? 4;
  const half = size / 2 + pad;
  return [
    {
      x: boss.x - half,
      y: boss.y - half,
      width: half * 2,
      height: half * 2,
    },
  ];
}

/**
 * Decide whether the scene should run a repath cycle this frame. Returns true
 * iff at least `intervalMs` has passed since the last repath. Frame-rate-
 * independent (uses `nowMs`, not delta).
 *
 * Centralising this lets tests verify the 0.5s cadence without faking timers.
 */
export function shouldRepath(nowMs: number, lastRepathMs: number, intervalMs: number = 500): boolean {
  return nowMs - lastRepathMs >= intervalMs;
}

/**
 * Pure A* result handler — applied as the findPath callback. Assigns the
 * resulting waypoint list onto the enemy when non-null + non-empty, otherwise
 * clears the enemy's existing path so it falls back to straight-line move.
 *
 * Exported so tests can drive it without instantiating Pathfinder.
 */
export function applyPathResult(
  enemy: { path: Array<{ x: number; y: number }> | null },
  result: PathResult,
): void {
  if (result === null || result.length === 0) {
    enemy.path = null;
    return;
  }
  // Drop the very first waypoint — it's the current cell centre, which the
  // enemy is already inside, and stepping toward it would briefly snap them
  // backward (visible jitter at high speeds). Pathfinder always returns at
  // least 2 nodes (start + end cell centres) when a path exists, so slicing
  // (1) leaves the useful "next" waypoint list.
  enemy.path = result.length > 1 ? result.slice(1) : result;
}

/**
 * Repath every enemy in `enemies` toward `target` via `pathfinder`. The
 * pathfinder MUST already have its obstacle grid set (caller should invoke
 * `pathfinder.setObstacles(...)` first when obstacles change). Returns the
 * number of findPath calls issued — useful for tests + perf telemetry.
 *
 * Implementation notes:
 * - We do NOT throttle batch-per-tick here. The pathfinder is constructed in
 *   sync mode for our small 800×600 grid (≤ ~30×20 cells with cellSize ≥ 32),
 *   which the wiki measured at < 1ms per find. 0.5s × ≤20 enemies × < 1ms
 *   fits the 60FPS frame budget. If profiling later shows spikes, swap to
 *   async + a round-robin pump here without changing the scene API.
 * - `applyPathResult` runs synchronously inside findPath's callback (because
 *   we built the pathfinder with enableSync: default true), so by the time
 *   this function returns every enemy.path is already updated.
 */
export function repathEnemiesToward(
  pathfinder: Pathfinder,
  enemies: Array<{ x: number; y: number; alive: boolean; path: Array<{ x: number; y: number }> | null }>,
  target: { x: number; y: number },
): number {
  let count = 0;
  for (const e of enemies) {
    if (!e.alive) continue;
    pathfinder.findPath({ x: e.x, y: e.y }, target, (res) => applyPathResult(e, res));
    count++;
  }
  return count;
}

/**
 * When the boss dies / no obstacles remain, clear every enemy's path so they
 * revert to straight-line motion (their original `vx, vy`). Returns the
 * number of enemies whose path was cleared.
 *
 * Called by the scene on the boss-defeat transition so the leftover grunts
 * don't keep walking the old curved path past where the boss used to be.
 */
export function clearAllEnemyPaths(
  enemies: Array<{ path: Array<{ x: number; y: number }> | null }>,
): number {
  let cleared = 0;
  for (const e of enemies) {
    if (e.path !== null) {
      e.path = null;
      cleared++;
    }
  }
  return cleared;
}

/**
 * Grid cell size used by the battle-scene Pathfinder instance. Chosen so a
 * 800×600 world maps to 25×19 cells — small enough that easystarjs's sync
 * findPath stays well under 1ms even with 20 grunts, while still leaving the
 * boss bbox (≥ 80px) covering a 3×3 block of blocked cells (enough that A*
 * actually has to route around rather than grid-snap through a corner).
 */
export const BATTLE_PATHFINDER_CELL_SIZE = 32;

/**
 * Interval between repath cycles in ms. Wiki G06 acceptance: ~0.5s.
 */
export const BATTLE_REPATH_INTERVAL_MS = 500;
